import { createContext, useContext, useEffect } from 'react'

/**
 * Lets a page hide the "Application Progress" panel for one of its own states.
 *
 * Exactly one screen needs this, and it is the reason the layout cannot decide
 * on the pathname alone: `/apply/:appRefNo/submitted` renders TWO designed
 * frames. While the finalise call is in flight it is "Submitting Application",
 * which keeps the panel and reads 7 of 7; once it returns it is "Application
 * Submitted", which the design draws as a centred card with no panel at all.
 * Same route, two answers.
 *
 * A context rather than a route split because moving the interstitial onto its
 * own route would mean moving the finalise call with it, which is API work.
 */
const StepperSuppressionContext = createContext<(suppressed: boolean) => void>(() => {})

export const StepperSuppressionProvider = StepperSuppressionContext.Provider

/**
 * Hides the progress panel while `suppressed` is true, and restores it when the
 * page unmounts — so a screen that forgets to reset cannot strand the next one
 * without its stepper.
 */
export function useSuppressStepper(suppressed: boolean): void {
  const setSuppressed = useContext(StepperSuppressionContext)

  useEffect(() => {
    setSuppressed(suppressed)
    return () => {
      setSuppressed(false)
    }
  }, [setSuppressed, suppressed])
}
